import Input from '@superfunnel/ui-core/v2/Input';
import { extractDomainName, generateSlug } from '@superfunnel/utils';
import { useCallback, useState } from 'react';
import { StepContentLayout } from './StepContentLayout';
import { StepContentProps } from './StepContentProps';

export const OnboardingStep1 = ({
  data,
  onChange,
  onNext,
  isLoading = false,
  disabled,
}: StepContentProps) => {
  const [urlUnfocused, setUrlUnfocused] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const handleUrlChange = (newUrl: string) => {
    onChange((prev) => {
      const updates: any = { websiteUrl: newUrl };

      // Auto-populate name if not touched
      if (!nameTouched) {
        const extractedName = extractDomainName(newUrl);
        updates.name = extractedName;
        // Auto-generate slug from extracted name if slug not touched
        if (!slugTouched) {
          updates.slug = generateSlug(extractedName);
        }
      } else if (!slugTouched && prev.name) {
        // If name exists and slug not touched, regenerate slug
        updates.slug = generateSlug(prev.name);
      }

      return { ...prev, ...updates };
    });
  };

  const handleNameChange = (newName: string) => {
    setNameTouched(true);
    onChange((prev) => {
      const updates: any = { name: newName };

      // Auto-generate slug if not touched
      if (!slugTouched && newName) {
        updates.slug = generateSlug(newName);
      }

      return { ...prev, ...updates };
    });
  };

  const handleSlugChange = (newSlug: string) => {
    setSlugTouched(true);
    onChange((prev) => ({ ...prev, slug: newSlug }));
  };

  const isValidUrl = useCallback(() => {
    if (!data?.websiteUrl)
      return { status: 'error' as const, message: 'URL is required' };

    try {
      const url = data?.websiteUrl || '';
      new URL(url.startsWith('http') ? url : `https://${url}`);
      // add valida url logic using regex. it should be a valid website url
      const urlPattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
          '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*' + // port and path
          '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-zA-Z\\d_]*)?$',
      );

      if (!urlPattern.test(url.startsWith('http') ? url : `https://${url}`)) {
        return { status: 'error' as const, message: 'Invalid URL' };
      }

      return undefined;
    } catch {
      return { status: 'error' as const, message: 'Invalid URL' };
    }
  }, [data?.websiteUrl]);

  return (
    <StepContentLayout
      showBackButton={false}
      showNextButton
      onNext={onNext}
      nextButtonDisabled={!data?.websiteUrl}
      isLoading={isLoading}
      disabled={disabled}
    >
      <div className="flex w-full flex-col items-stretch gap-2">
        <Input
          title="Your site"
          type="url"
          value={data?.websiteUrl || ''}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="www.yourcompany.com"
          className="w-full"
          validation={urlUnfocused ? isValidUrl() : undefined}
          onBlur={() => setUrlUnfocused(true)}
          required
          disabled={disabled}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            title="Company name"
            value={data?.name || ''}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Company Inc."
            wrapperClassName="flex-1"
            disabled={disabled}
          />
          <Input
            title="Slug"
            value={data?.slug || ''}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="yourcompany"
            wrapperClassName="flex-1"
            disabled={disabled}
          />
        </div>
      </div>
    </StepContentLayout>
  );
};
